import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, Loader2, ShieldCheck, Zap } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { PageLoading, ErrorState } from "../components/States";
import { api } from "../lib/api";
import { fmtCents, setPageTitle } from "../lib/format";

const STATES = {
  not_connected: { label: "Not connected", cls: "text-zinc-400", hint: "Connect Stripe to receive payouts from your bookings." },
  setup_required: { label: "Setup required", cls: "text-amber-400", hint: "Finish the Stripe onboarding steps to enable payouts." },
  verification_pending: { label: "Verification pending", cls: "text-blue-400", hint: "Stripe is verifying your details. This usually takes a short time." },
  action_required: { label: "Action required", cls: "text-amber-400", hint: "Stripe needs more information from you to keep payouts active." },
  restricted: { label: "Restricted", cls: "text-red-400", hint: "Your payout account is restricted. Open Stripe to resolve it." },
  payouts_disabled: { label: "Payouts disabled", cls: "text-red-400", hint: "Charges work but payouts are off until Stripe requirements are met." },
  ready: { label: "Ready", cls: "text-emerald-400", hint: "Your account can receive payouts." },
};

// Stripe Connect Express markets with local payout rails. Stripe-hosted onboarding
// renders each country's own bank fields automatically (IBAN for EU, routing+account
// for US, transit+institution for CA, sort code for GB, BSB for AU, ...). India is
// deliberately absent: Stripe Express is invite-only there — the backend maps any
// unsupported country to a clear 400.
const COUNTRIES = [
  ["CA", "Canada"], ["US", "United States"], ["GB", "United Kingdom"],
  ["AU", "Australia"], ["NZ", "New Zealand"], ["IE", "Ireland"],
  ["DE", "Germany"], ["FR", "France"], ["ES", "Spain"], ["IT", "Italy"],
  ["NL", "Netherlands"], ["BE", "Belgium"], ["AT", "Austria"], ["PT", "Portugal"],
  ["FI", "Finland"], ["SE", "Sweden"], ["DK", "Denmark"], ["NO", "Norway"],
  ["CH", "Switzerland"], ["SG", "Singapore"],
];

export default function Payouts() {
  const [status, setStatus] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [error, setError] = useState(null);
  const [country, setCountry] = useState("CA");
  const [bizType, setBizType] = useState("individual");
  const [busy, setBusy] = useState(false);
  const [cashoutBusy, setCashoutBusy] = useState(false);
  const [params] = useSearchParams();

  const load = () => {
    setError(null);
    api.connectStatus().then(setStatus).catch((e) => setError(e.message));
    api.wallet().then(setWallet).catch(() => {});
  };
  useEffect(() => {
    setPageTitle("Payouts");
    load();
    if (params.get("return")) toast.success("Stripe onboarding updated — status refreshed");
  }, []); // eslint-disable-line

  const onboard = async () => {
    setBusy(true);
    try {
      const r = await api.connectOnboarding(country, undefined, bizType);
      window.location.href = r.url; // Stripe-hosted onboarding — bank details never touch MyRedPark.
    } catch (e) { toast.error(e.message); setBusy(false); }
  };

  const cashout = async () => {
    setCashoutBusy(true);
    try {
      const r = await api.cashoutInstant();
      toast.success(`Payout of ${fmtCents(r.amount_cents)} sent (bank ••••${r.bank_last4 || "????"})`);
      load();
    } catch (e) { toast.error(e.message); } finally { setCashoutBusy(false); }
  };

  const st = STATES[status?.state] || STATES.not_connected;

  return (
    <div className="min-h-screen flex flex-col" data-testid="payouts-page">
      <Navbar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
        <p className="section-label">Host</p>
        <h1 className="type-display mt-3">Payouts</h1>
        <p className="mt-1 text-sm text-zinc-400">Secure payout setup — connect your bank account through Stripe's encrypted onboarding. Your bank details are entered on Stripe's secure pages and never touch MyRedPark.</p>

        {error ? <div className="mt-8"><ErrorState body={error} onRetry={load} /></div> : !status ? <PageLoading label="Checking payout status…" /> : (
          <div className="mt-8 space-y-6">
            <div className="card-dark p-6" data-testid="connect-status-card">
              <div className="flex items-center justify-between">
                <h2 className="font-bold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#FF3B45]" /> Stripe Connect</h2>
                <span className={`font-semibold text-sm ${st.cls}`} data-testid="payout-state">{st.label}</span>
              </div>
              <p className="mt-2 text-sm text-zinc-400">{st.hint}</p>
              {status.connected && (
                <div className="mt-4 grid sm:grid-cols-3 gap-3 text-xs">
                  <div className="p-3 rounded-lg bg-[#080B0D] border border-[#27272A]"><p className="text-zinc-500">Charges</p><p className={status.charges_enabled ? "text-emerald-400" : "text-zinc-400"}>{status.charges_enabled ? "Enabled" : "Off"}</p></div>
                  <div className="p-3 rounded-lg bg-[#080B0D] border border-[#27272A]"><p className="text-zinc-500">Payouts</p><p className={status.payouts_enabled ? "text-emerald-400" : "text-zinc-400"}>{status.payouts_enabled ? "Enabled" : "Off"}</p></div>
                  <div className="p-3 rounded-lg bg-[#080B0D] border border-[#27272A]"><p className="text-zinc-500">Bank</p><p className="text-zinc-200">{status.bank_name ? `${status.bank_name} ••••${status.bank_last4 || ""}` : "—"}</p></div>
                </div>
              )}
              {status.requirements?.currently_due?.length > 0 && (
                <p className="mt-3 text-xs text-amber-400" data-testid="payout-requirements">{status.requirements.currently_due.length} requirement(s) outstanding — open Stripe to continue.</p>
              )}
              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                {!status.connected && (
                  <>
                    <select value={country} onChange={(e) => setCountry(e.target.value)} data-testid="payout-country" aria-label="Country"
                            className="input-dark sm:!w-56">
                      {COUNTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                    <select value={bizType} onChange={(e) => setBizType(e.target.value)} data-testid="payout-account-type" aria-label="Account type"
                            className="input-dark sm:!w-56">
                      <option value="individual">Individual host</option>
                      <option value="company">Business / company</option>
                    </select>
                  </>
                )}
                <button onClick={onboard} disabled={busy} data-testid="stripe-onboarding-btn" className="btn-primary text-sm">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                  {status.connected ? "Open Stripe onboarding" : "Connect with Stripe"}
                </button>
              </div>
            </div>

            <div className="card-dark p-6" data-testid="cashout-card">
              <h2 className="font-bold flex items-center gap-2"><Zap className="w-5 h-5 text-[#FF3B45]" /> Cash out</h2>
              {wallet ? (
                <>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="p-4 rounded-xl bg-[#080B0D] border border-[#27272A]">
                      <p className="label-dark !mb-1">Available</p>
                      <p className="font-display font-extrabold text-2xl" data-testid="wallet-available">{fmtCents(wallet.balance_cents)}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-[#080B0D] border border-[#27272A]">
                      <p className="label-dark !mb-1">Processing</p>
                      <p className="font-display font-extrabold text-2xl" data-testid="wallet-pending">{fmtCents(wallet.pending_cents)}</p>
                    </div>
                  </div>
                  <button onClick={cashout} disabled={cashoutBusy || !status.payouts_enabled || wallet.balance_cents < wallet.minimum_cashout_cents}
                          data-testid="instant-cashout-btn" className="btn-primary w-full mt-5 text-sm">
                    {cashoutBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    Instant cashout ({fmtCents(wallet.instant_fee_cents)} fee · min {fmtCents(wallet.minimum_cashout_cents)})
                  </button>
                  {!status.payouts_enabled && <p className="mt-2 text-xs text-amber-400" data-testid="cashout-ineligible-note">Complete payout setup to enable cashouts.</p>}
                </>
              ) : <PageLoading label="Loading wallet…" />}
            </div>
            <p className="text-xs text-zinc-500" data-testid="payout-security-note">
              Payout availability and timing are determined by your payment provider. Earnings become available in your balance when a booking is completed or settled.
            </p>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
